import { useOutletContext } from "react-router-dom";
import { Download, Image, FileText, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kickoff-central-10/artifacts/72zh29ie_Middel%2020%40300x.png";

const logos = [
  {
    id: "logo-full",
    title: "Hoofdlogo",
    description: "Het officiële clublogo van Futsal Gent in hoge resolutie (PNG, transparante achtergrond).",
    url: LOGO_URL,
    filename: "futsal-gent-logo.png",
    format: "PNG",
  },
];

const guidelines = [
  "Gebruik het logo altijd in zijn originele verhoudingen. Vervorm of rek het logo niet uit.",
  "Zorg voor voldoende witruimte rondom het logo.",
  "Wijzig de kleuren van het logo niet.",
  "Plaats het logo niet op een drukke achtergrond waardoor het moeilijk leesbaar wordt.",
  "Voor persberichten en mediaverzoeken, neem contact op via info@futsalgent.be.",
];

export default function LogoDownload() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen" data-testid="logo-download-page">
      {/* Hero */}
      <section className={`relative py-20 ${isDark ? 'bg-gradient-to-b from-slate-900 to-[hsl(224,71%,4%)]' : 'bg-gradient-to-b from-slate-200 to-slate-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className={`text-4xl md:text-5xl font-black font-['Chivo'] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Logo Downloads
          </h1>
          <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Download het officiële logo van Futsal Gent voor gebruik in pers, media en partnermateriaal.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Logo Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {logos.map((logo) => (
            <Card key={logo.id} className={`overflow-hidden ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
              {/* Preview */}
              <div className={`flex items-center justify-center p-8 min-h-[250px] ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
                <img
                  src={logo.url}
                  alt={logo.title}
                  className="max-h-48 w-auto object-contain"
                />
              </div>
              <CardHeader>
                <CardTitle className={`font-['Chivo'] flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <FileText className={`h-5 w-5 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`} />
                  {logo.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {logo.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium px-2 py-1 rounded ${isDark ? 'bg-white/10 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                    {logo.format}
                  </span>
                  <Button
                    onClick={() => handleDownload(logo.url, logo.filename)}
                    className="bg-[hsl(48,96%,53%)] hover:bg-[hsl(48,96%,45%)] text-slate-900 font-semibold"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Dark background preview */}
          <Card className={`overflow-hidden ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <div className="flex items-center justify-center p-8 min-h-[250px] bg-[#0A1A5C]">
              <img
                src={LOGO_URL}
                alt="Logo op donkere achtergrond"
                className="max-h-48 w-auto object-contain"
              />
            </div>
            <CardHeader>
              <CardTitle className={`font-['Chivo'] flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <FileText className={`h-5 w-5 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`} />
                Voorbeeld op donkere achtergrond
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Zo ziet het logo eruit op een donkere achtergrond. Ideaal voor gebruik op donkere media en drukwerk.
              </p>
            </CardContent>
          </Card>

          {/* Light background preview */}
          <Card className={`overflow-hidden ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <div className="flex items-center justify-center p-8 min-h-[250px] bg-white">
              <img
                src={LOGO_URL}
                alt="Logo op lichte achtergrond"
                className="max-h-48 w-auto object-contain"
              />
            </div>
            <CardHeader>
              <CardTitle className={`font-['Chivo'] flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <FileText className={`h-5 w-5 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`} />
                Voorbeeld op lichte achtergrond
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Zo ziet het logo eruit op een lichte achtergrond. Ideaal voor gebruik op wit papier en licht drukwerk.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Usage Guidelines */}
        <Card className={isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}>
          <CardHeader>
            <CardTitle className={`font-['Chivo'] text-2xl flex items-center gap-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Info className={`h-6 w-6 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`} />
              Gebruiksrichtlijnen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Houd je aan de volgende richtlijnen bij het gebruik van het Futsal Gent logo:
            </p>
            <ul className="space-y-3">
              {guidelines.map((guideline, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-[hsl(48,96%,53%)] text-slate-900' : 'bg-amber-500 text-white'}`}>
                    {index + 1}
                  </span>
                  <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{guideline}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
